// Ejemplos: Sensores.
import { at, part, wires, UNO } from './helpers.js';

// Protoboard a la derecha del Uno: los cables salen cortos de sus dos filas de pines.
const BB = { id: 'bb1', type: 'breadboard', x: 115, z: 0, rot: 0, props: { size: 'half' } };

/** Agujero del riel (tp/tn arriba, bp/bn abajo) en la misma columna que `col`. */
function rail(id, col) {
  const idx = col - 2;
  if (idx < 0 || idx % 6 === 5) throw new Error(`La columna ${col} no tiene agujero en el riel`);
  return `bb1:${id}${Math.floor(idx / 6) * 5 + (idx % 6) + 1}`;
}

const CAT = 'Sensores';

export const EXAMPLES = [
  // Distancia -------------------------------------------------------------------------------------
  {
    id: 'ultrasonico',
    category: CAT,
    name: 'Medidor de distancia (HC-SR04)',
    description: 'Un pulso de 10 µs en TRIG dispara la medición y pulseIn() mide cuánto dura ECHO en alto. Cambia la distancia en el panel del sensor.',
    project: {
      name: 'Medidor de distancia',
      files: {
        'sketch.ino': `// Medidor de distancia con el sensor ultrasónico HC-SR04.
// TRIG va al pin 9 y ECHO al pin 10. Selecciona el sensor y cambia la distancia en su panel.

const int TRIG = 9;
const int ECHO = 10;

void setup() {
  Serial.begin(9600);
  pinMode(TRIG, OUTPUT);
  pinMode(ECHO, INPUT);
}

void loop() {
  // Pulso de 10 µs en TRIG para disparar una medición
  digitalWrite(TRIG, LOW);
  delayMicroseconds(2);
  digitalWrite(TRIG, HIGH);
  delayMicroseconds(10);
  digitalWrite(TRIG, LOW);

  // ECHO queda en alto el tiempo de ida y vuelta del sonido (máximo 30 ms ≈ 5 m)
  unsigned long us = pulseIn(ECHO, HIGH, 30000);
  if (us == 0) {
    Serial.println("Fuera de alcance");
  } else {
    float cm = us / 58.3;   // el sonido tarda 58.3 µs en ir y volver 1 cm
    Serial.print("Distancia: ");
    Serial.print(cm, 1);
    Serial.println(" cm");
  }
  delay(100);   // deja que se apaguen los ecos antes de la siguiente medición
}
`,
      },
      parts: [UNO, BB, part('us1', 'hc-sr04', { distance: 30 }, at(BB, 'j8', 'hc-sr04'))],
      wires: wires([
        ['bb1:f8', 'uno:5V', 'red'],
        ['bb1:f11', 'uno:GND.2', 'black'],
        ['bb1:f9', 'uno:9', 'yellow'],
        ['bb1:f10', 'uno:10', 'green'],
      ]),
    },
  },
  {
    id: 'estacionamiento',
    category: CAT,
    name: 'Sensor de estacionamiento (NewPing)',
    description: 'La librería NewPing mide la distancia y un zumbador activo pita más seguido cuanto más cerca está el obstáculo.',
    project: {
      name: 'Sensor de estacionamiento',
      files: {
        'sketch.ino': `// Sensor de estacionamiento: pitidos más seguidos cuanto más cerca está el obstáculo.
// Librería NewPing (TRIG = 9, ECHO = 10) y un zumbador activo en el pin 8.
// NewPing usa el Timer2, igual que tone(): por eso el zumbador es activo (se enciende con HIGH).
#include <NewPing.h>

const int TRIG = 9, ECHO = 10, ZUMBADOR = 8;
NewPing sonar(TRIG, ECHO, 200);   // distancia máxima: 200 cm

void setup() {
  Serial.begin(9600);
  pinMode(ZUMBADOR, OUTPUT);
}

void loop() {
  unsigned int cm = sonar.ping_cm();   // 0 si no hay eco dentro de los 200 cm
  Serial.print("Distancia: ");
  if (cm == 0) Serial.println("libre");
  else {
    Serial.print(cm);
    Serial.println(" cm");
  }

  if (cm > 0 && cm <= 10) {            // muy cerca: tono continuo
    digitalWrite(ZUMBADOR, HIGH);
    delay(100);
  } else if (cm > 10 && cm < 60) {     // entre más cerca, más seguido
    digitalWrite(ZUMBADOR, HIGH);
    delay(60);
    digitalWrite(ZUMBADOR, LOW);
    delay(map(cm, 10, 60, 80, 600));
  } else {
    digitalWrite(ZUMBADOR, LOW);
    delay(100);
  }
}
`,
      },
      parts: [
        UNO,
        BB,
        part('us1', 'hc-sr04', { distance: 25 }, at(BB, 'j8', 'hc-sr04')),
        part('buzzer1', 'buzzer', { mode: 'active' }, at(BB, 'j20', 'buzzer')),
      ],
      wires: wires([
        ['bb1:f8', 'uno:5V', 'red'],
        ['bb1:f11', 'uno:GND.2', 'black'],
        ['bb1:f9', 'uno:9', 'yellow'],
        ['bb1:f10', 'uno:10', 'green'],
        ['bb1:f20', 'uno:GND.3', 'black'],
        ['bb1:f23', 'uno:8', 'orange'],
      ]),
    },
  },

  {
    id: 'ultrasonico-un-pin',
    category: CAT,
    name: 'Distancia con un solo pin (HC-SR04 + NewPing)',
    description: 'TRIG y ECHO unidos en la protoboard y conectados al pin 7: NewPing manda el pulso, cambia el pin a entrada y mide el eco.',
    project: {
      name: 'Distancia con un solo pin',
      files: {
        'sketch.ino': `// Medidor de distancia con el HC-SR04 usando UN solo pin del Arduino (librería NewPing).
// TRIG y ECHO están unidos con un puente en la protoboard y van al pin 7. Cambia la distancia en el panel del sensor.
// NewPing lo hace así: sube el pin 12 µs (TRIG), lo cambia a entrada y mide cuánto dura el eco.
// (Muchos montajes ponen una resistencia de 1 a 2.2 kΩ entre ECHO y TRIG para proteger los pines; también funciona.)
#include <NewPing.h>

const int PIN_SONAR = 7;
const int DISTANCIA_MAX = 200;                          // cm
NewPing sonar(PIN_SONAR, PIN_SONAR, DISTANCIA_MAX);     // el mismo pin para TRIG y ECHO = modo de un pin

void setup() {
  Serial.begin(9600);
  pinMode(LED_BUILTIN, OUTPUT);
}

void loop() {
  unsigned int cm = sonar.ping_cm();   // 0 si no hay eco dentro del máximo
  if (cm == 0) {
    Serial.println("Sin objeto a la vista");
  } else {
    Serial.print("Distancia: ");
    Serial.print(cm);
    Serial.println(" cm");
  }
  digitalWrite(LED_BUILTIN, cm > 0 && cm < 20);   // el LED L se enciende a menos de 20 cm
  delay(100);   // deja que se apaguen los ecos antes de la siguiente medición
}
`,
      },
      parts: [UNO, BB, part('us1', 'hc-sr04', { distance: 40 }, at(BB, 'j8', 'hc-sr04'))],
      wires: wires([
        ['bb1:f8', 'uno:5V', 'red'],
        ['bb1:f11', 'uno:GND.2', 'black'],
        ['bb1:f9', 'bb1:f10', 'blue'],
        ['bb1:f9', 'uno:7', 'yellow'],
      ]),
    },
  },

  // Temperatura y humedad --------------------------------------------------------------------------
  {
    id: 'dht22',
    category: CAT,
    name: 'Temperatura y humedad (DHT22)',
    description: 'La librería DHT lee el DHT22 por un solo cable cada 2 s y calcula la sensación térmica.',
    project: {
      name: 'DHT22',
      files: {
        'sketch.ino': `// Temperatura y humedad con el DHT22 (librería DHT de Adafruit). Datos en el pin 2.
// Cambia la temperatura y la humedad en el panel del sensor mientras simulas.
#include <DHT.h>

DHT dht(2, DHT22);

void setup() {
  Serial.begin(9600);
  dht.begin();
  Serial.println("DHT22 listo");
}

void loop() {
  delay(2000);   // el DHT22 entrega una medición nueva cada 2 s como máximo
  float h = dht.readHumidity();
  float t = dht.readTemperature();
  if (isnan(h) || isnan(t)) {
    Serial.println("No se pudo leer el DHT22");
    return;
  }
  float st = dht.computeHeatIndex(t, h, false);
  Serial.print("Temperatura: ");
  Serial.print(t, 1);
  Serial.print(" °C   Humedad: ");
  Serial.print(h, 1);
  Serial.print(" %   Sensación: ");
  Serial.print(st, 1);
  Serial.println(" °C");
}
`,
      },
      parts: [UNO, BB, part('dht1', 'dht22', { temperature: 24, humidity: 55 }, at(BB, 'j8', 'dht22'))],
      wires: wires([
        ['bb1:f8', 'uno:5V', 'red'],
        ['bb1:f11', 'uno:GND.2', 'black'],
        ['bb1:f9', 'uno:2', 'green'],
      ]),
    },
  },
  {
    id: 'dht11-lcd',
    category: CAT,
    name: 'Estación de clima (DHT11 y LCD)',
    description: 'El DHT11 mide temperatura y humedad, y una LCD 16×2 I²C las muestra con un símbolo de grado propio.',
    project: {
      name: 'Estación de clima',
      files: {
        'sketch.ino': `// Estación de clima: DHT11 en el pin 2 y una LCD 16x2 I2C (SDA = A4, SCL = A5).
// La LCD toma 5 V de IOREF, que en el Uno está unido a 5 V.
#include <Wire.h>
#include <LiquidCrystal_I2C.h>
#include <DHT.h>

DHT dht(2, DHT11);
LiquidCrystal_I2C lcd(0x27, 16, 2);

byte grado[8] = { 0b00110, 0b01001, 0b01001, 0b00110, 0b00000, 0b00000, 0b00000, 0b00000 };

void setup() {
  dht.begin();
  lcd.init();
  lcd.backlight();
  lcd.createChar(0, grado);
  lcd.print("Estacion DHT11");
}

void loop() {
  delay(1200);   // el DHT11 mide una vez por segundo como máximo
  float h = dht.readHumidity();
  float t = dht.readTemperature();
  lcd.clear();
  if (isnan(h) || isnan(t)) {
    lcd.print("Error del sensor");
    return;
  }
  lcd.print("Temp:    ");
  lcd.print(t, 0);
  lcd.write(byte(0));
  lcd.print("C");
  lcd.setCursor(0, 1);
  lcd.print("Humedad: ");
  lcd.print(h, 0);
  lcd.print(" %");
}
`,
      },
      parts: [
        UNO,
        BB,
        part('dht1', 'dht11', { temperature: 27, humidity: 48 }, at(BB, 'j8', 'dht11')),
        part('lcd1', 'lcd-i2c', { size: '16x2', color: 'blue' }, { x: 22, z: 84, rot: 0 }),
      ],
      wires: wires([
        ['bb1:f8', 'uno:5V', 'red'],
        ['bb1:f11', 'uno:GND.2', 'black'],
        ['bb1:f9', 'uno:2', 'green'],
        ['lcd1:GND', 'uno:GND.3', 'black'],
        ['lcd1:VCC', 'uno:IOREF', 'red'],
        ['lcd1:SDA', 'uno:A4', 'blue'],
        ['lcd1:SCL', 'uno:A5', 'yellow'],
      ]),
    },
  },

  // Movimiento ----------------------------------------------------------------------------------------
  {
    id: 'pir-alarma',
    category: CAT,
    name: 'Alarma con sensor PIR',
    description: 'Mantén presionado el domo del HC-SR501 para simular movimiento: se enciende el LED y suena el zumbador mientras OUT esté en alto. Al encender, el sensor se inicializa unos segundos.',
    project: {
      name: 'Alarma PIR',
      files: {
        'sketch.ino': `// Alarma con sensor de movimiento PIR (HC-SR501): OUT en el pin 2.
// Mantén presionado el domo blanco para simular movimiento. El retardo y el modo se ajustan en su panel.
// Al recibir alimentación el HC-SR501 se inicializa: el real tarda ~1 min, no detecta bien y su salida puede
// activarse sola 0–3 veces. Aquí «Calentamiento: rápido» lo reduce a ~6 s. El programa espera a que termine
// (el LED parpadea) y a que OUT esté en bajo antes de vigilar.
const int PIR = 2, LED = 13, ZUMBADOR = 8;
const unsigned long CALIBRACION_MS = 7000;   // con el módulo real: 60000
bool vigilando = false;
int anterior = LOW;

void setup() {
  Serial.begin(9600);
  pinMode(PIR, INPUT);
  pinMode(LED, OUTPUT);
  pinMode(ZUMBADOR, OUTPUT);
  Serial.println("Calibrando el sensor...");
}

void loop() {
  if (!vigilando) {
    digitalWrite(LED, (millis() / 400) % 2);   // parpadea mientras el módulo se inicializa
    if (millis() > CALIBRACION_MS && digitalRead(PIR) == LOW) {
      vigilando = true;
      digitalWrite(LED, LOW);
      Serial.println("Vigilando...");
    }
    return;
  }
  int estado = digitalRead(PIR);
  digitalWrite(LED, estado);
  // Alarma intermitente mientras haya detección
  digitalWrite(ZUMBADOR, estado == HIGH && (millis() / 150) % 2);
  if (estado != anterior) {
    Serial.println(estado == HIGH ? "¡Movimiento detectado!" : "Sin movimiento");
    anterior = estado;
  }
}
`,
      },
      parts: [
        UNO,
        BB,
        part('pir1', 'pir', { delay: 0.111, mode: 'repeat' }, at(BB, 'a8', 'pir')),
        part('led1', 'led', { color: 'red' }, at(BB, 'h20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB, 'i21', 'resistor')),
        part('buzzer1', 'buzzer', { mode: 'active' }, at(BB, 'j27', 'buzzer')),
      ],
      wires: wires([
        ['bb1:e8', 'uno:5V', 'red'],
        ['bb1:e10', 'uno:GND.1', 'black'],
        ['bb1:e9', 'uno:2', 'yellow'],
        ['bb1:f20', 'uno:GND.2', 'black'],
        ['bb1:f25', 'uno:13', 'green'],
        ['bb1:f27', 'uno:GND.3', 'black'],
        ['bb1:f30', 'uno:8', 'orange'],
      ]),
    },
  },

  // Luz ------------------------------------------------------------------------------------------------
  {
    id: 'ldr-luz-nocturna',
    category: CAT,
    name: 'Luz nocturna (fotorresistencia)',
    description: 'Una LDR y una resistencia de 10 kΩ forman un divisor en A0; con menos luz el LED del pin 9 brilla más.',
    project: {
      name: 'Luz nocturna',
      files: {
        'sketch.ino': `// Luz nocturna: con poca luz el LED del pin 9 brilla más.
// Divisor: 5 V → fotorresistencia → A0 → 10 kΩ → GND (más luz = lectura más alta).
const int LDR = A0, LED = 9;

void setup() {
  Serial.begin(9600);
  pinMode(LED, OUTPUT);
}

void loop() {
  int luz = analogRead(LDR);
  int brillo = constrain(map(luz, 150, 800, 255, 0), 0, 255);
  analogWrite(LED, brillo);
  Serial.print("Luz: ");
  Serial.print(luz);
  Serial.print("   LED: ");
  Serial.println(brillo);
  delay(200);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('ldr1', 'photoresistor', { light: 0.6 }, at(BB, 'j10', 'photoresistor')),
        part('r1', 'resistor', { value: 10000 }, at(BB, 'i11', 'resistor')),
        part('led1', 'led', { color: 'yellow' }, at(BB, 'h20', 'led')),
        part('r2', 'resistor', { value: 220 }, at(BB, 'g21', 'resistor')),
      ],
      wires: wires([
        ['bb1:f10', 'uno:5V', 'red'],
        ['bb1:f15', 'uno:GND.2', 'black'],
        ['bb1:f11', 'uno:A0', 'orange'],
        ['bb1:f25', 'uno:9', 'blue'],
        ['bb1:f20', 'uno:GND.3', 'black'],
      ]),
    },
  },
  {
    id: 'modulo-luz',
    category: CAT,
    name: 'Lámpara automática (módulo de luz)',
    description: 'El módulo LDR + LM393 da AO (analógica) y DO (umbral de la perilla): con poca luz DO sube y se enciende el LED L.',
    project: {
      name: 'Lámpara automática',
      files: {
        'sketch.ino': `// Lámpara automática con el módulo de luz (LDR + comparador LM393).
// AO baja con más luz; DO está en HIGH cuando la luz no alcanza el umbral de la perilla azul.
const int AO_PIN = A0, DO_PIN = 7, LAMPARA = LED_BUILTIN;

void setup() {
  Serial.begin(9600);
  pinMode(DO_PIN, INPUT);
  pinMode(LAMPARA, OUTPUT);
}

void loop() {
  int luz = analogRead(AO_PIN);
  bool oscuro = digitalRead(DO_PIN) == HIGH;
  digitalWrite(LAMPARA, oscuro);
  Serial.print("AO: ");
  Serial.print(luz);
  Serial.println(oscuro ? "   oscuro: lámpara encendida" : "   hay luz");
  delay(250);
}
`,
      },
      parts: [UNO, part('luz1', 'photoresistor-module', { light: 0.55, threshold: 0.5 }, { x: 92, z: 8, rot: 0 })],
      wires: wires([
        ['luz1:VCC', 'uno:5V', 'red'],
        ['luz1:GND', 'uno:GND.2', 'black'],
        ['luz1:DO', 'uno:7', 'green'],
        ['luz1:AO', 'uno:A0', 'orange'],
      ]),
    },
  },

  // Temperatura analógica ----------------------------------------------------------------------------
  {
    id: 'ntc-termometro',
    category: CAT,
    name: 'Termómetro con termistor NTC',
    description: 'Un NTC de 10 kΩ (B = 3950) en divisor con 10 kΩ: la ecuación Beta convierte la lectura de A0 en grados.',
    project: {
      name: 'Termómetro NTC',
      files: {
        'sketch.ino': `// Termómetro con un termistor NTC de 10 kΩ (B = 3950).
// Divisor: 5 V → 10 kΩ → A0 → NTC → GND.
const float R_FIJA = 10000.0;   // resistencia del divisor
const float R25 = 10000.0;      // NTC a 25 °C
const float BETA = 3950.0;

void setup() {
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(A0);
  float r = R_FIJA * lectura / (1023.0 - lectura);                   // resistencia del NTC
  float kelvin = 1.0 / (log(r / R25) / BETA + 1.0 / 298.15);         // ecuación Beta
  float celsius = kelvin - 273.15;
  Serial.print("R = ");
  Serial.print(r, 0);
  Serial.print(" ohm   T = ");
  Serial.print(celsius, 1);
  Serial.println(" °C");
  delay(500);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('r1', 'resistor', { value: 10000 }, at(BB, 'i6', 'resistor')),
        part('ntc1', 'ntc', { temperature: 25 }, at(BB, 'j10', 'ntc')),
      ],
      wires: wires([
        ['bb1:f6', 'uno:5V', 'red'],
        ['bb1:f10', 'uno:A0', 'orange'],
        ['bb1:f11', 'uno:GND.2', 'black'],
      ]),
    },
  },
  {
    id: 'ky013',
    category: CAT,
    name: 'Módulo termistor KY-013',
    description: 'El KY-013 trae el NTC y su resistencia de 10 kΩ: solo se conecta S a A0, el pin central a 5 V y − a GND.',
    project: {
      name: 'KY-013',
      files: {
        'sketch.ino': `// Módulo termistor KY-013: S en A0, pin central a 5 V y − a GND.
// En el módulo el NTC va entre + y S, y la resistencia fija de 10 kΩ entre S y −.
void setup() {
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(A0);
  float r = 10000.0 * (1023.0 / lectura - 1.0);                      // resistencia del NTC
  float celsius = 1.0 / (log(r / 10000.0) / 3950.0 + 1.0 / 298.15) - 273.15;
  Serial.print("Lectura: ");
  Serial.print(lectura);
  Serial.print("   Temperatura: ");
  Serial.print(celsius, 1);
  Serial.println(" °C");
  delay(500);
}
`,
      },
      parts: [UNO, part('ky1', 'ntc-module', { temperature: 25 }, { x: 80, z: 10, rot: 0 })],
      wires: wires([
        ['ky1:S', 'uno:A0', 'orange'],
        ['ky1:VCC', 'uno:5V', 'red'],
        ['ky1:GND', 'uno:GND.2', 'black'],
      ]),
    },
  },
  {
    id: 'lm35-tmp36',
    category: CAT,
    name: 'Termómetros analógicos (LM35 y TMP36)',
    description: 'El LM35 da 10 mV/°C y el TMP36 500 mV + 10 mV/°C: el mismo cálculo con distinto desplazamiento.',
    project: {
      name: 'LM35 y TMP36',
      files: {
        'sketch.ino': `// Dos sensores de temperatura analógicos: LM35 en A0 y TMP36 en A1.
// Visto de frente (cara plana), las patas son +Vs, Vout y GND.
void setup() {
  Serial.begin(9600);
}

float voltios(int pin) {
  return analogRead(pin) * 5.0 / 1024.0;
}

void loop() {
  float lm35 = voltios(A0) * 100.0;            // 10 mV por °C
  float tmp36 = (voltios(A1) - 0.5) * 100.0;   // 500 mV a 0 °C
  Serial.print("LM35: ");
  Serial.print(lm35, 1);
  Serial.print(" °C   TMP36: ");
  Serial.print(tmp36, 1);
  Serial.println(" °C");
  delay(500);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('lm1', 'lm35', { temperature: 25 }, at(BB, 'j6', 'lm35')),
        part('tmp1', 'tmp36', { temperature: 18 }, at(BB, 'j12', 'tmp36')),
      ],
      wires: wires([
        ['uno:5V', rail('bp', 2), 'red'],
        ['uno:GND.2', rail('bn', 3), 'black'],
        ['bb1:i6', rail('bp', 6), 'red'],
        ['bb1:i8', rail('bn', 8), 'black'],
        ['bb1:i12', rail('bp', 12), 'red'],
        ['bb1:i14', rail('bn', 14), 'black'],
        ['bb1:f7', 'uno:A0', 'orange'],
        ['bb1:f13', 'uno:A1', 'yellow'],
      ]),
    },
  },

  // Módulos con comparador ------------------------------------------------------------------------
  {
    id: 'aplauso',
    category: CAT,
    name: 'Interruptor por aplauso (KY-038)',
    description: 'Cada aplauso pone D0 en alto y cambia el LED integrado. Mantén presionado el micrófono para hacer ruido.',
    project: {
      name: 'Interruptor por aplauso',
      files: {
        'sketch.ino': `// Interruptor por aplauso con el sensor de sonido KY-038: D0 en el pin 2.
// Mantén presionado el micrófono (o el botón del panel) para hacer ruido.
// D0 da pulsos en alto mientras el sonido supera el umbral de la perilla azul.
const int D0_PIN = 2;
bool encendido = false;
unsigned long ultimo = 0;

void setup() {
  Serial.begin(9600);
  pinMode(D0_PIN, INPUT);
  pinMode(LED_BUILTIN, OUTPUT);
  Serial.println("Aplaude...");
}

void loop() {
  if (digitalRead(D0_PIN) == HIGH && millis() - ultimo > 500) {
    ultimo = millis();
    encendido = !encendido;
    digitalWrite(LED_BUILTIN, encendido);
    Serial.println(encendido ? "Aplauso: LED encendido" : "Aplauso: LED apagado");
  }
}
`,
      },
      parts: [UNO, part('mic1', 'sound-sensor', { level: 40, threshold: 0.55 }, { x: 95, z: 6, rot: 0 })],
      wires: wires([
        ['mic1:AO', 'uno:A0', 'orange'],
        ['mic1:GND', 'uno:GND.2', 'black'],
        ['mic1:VCC', 'uno:5V', 'red'],
        ['mic1:DO', 'uno:2', 'green'],
      ]),
    },
  },
  {
    id: 'detector-gas',
    category: CAT,
    name: 'Detector de gas (MQ-2)',
    description: 'AO sube con la concentración de gas; cuando pasa el umbral de la perilla DO baja y suena la alarma. Al encender, AO arranca alto mientras el sensor se calienta.',
    project: {
      name: 'Detector de gas',
      files: {
        'sketch.ino': `// Detector de gas con el MQ-2: AO en A0, DO en el pin 7 y un zumbador activo en el pin 8.
// Sube la concentración de gas en el panel del sensor.
// Al energizarlo el MQ-2 está frío: AO arranca alto (y DO da LOW) y baja mientras se calienta, minutos en el
// real; aquí «Calentamiento: rápido» lo reduce a ~17 s. Hasta entonces no se da ninguna alarma.
const int AO_PIN = A0, DO_PIN = 7, ALARMA = 8;
const unsigned long CALENTAMIENTO_MS = 20000;   // con el sensor real: varios minutos

void setup() {
  Serial.begin(9600);
  pinMode(DO_PIN, INPUT);
  pinMode(ALARMA, OUTPUT);
}

void loop() {
  int nivel = analogRead(AO_PIN);
  if (millis() < CALENTAMIENTO_MS) {
    Serial.print("Calentando... AO = ");
    Serial.println(nivel);
    delay(500);
    return;
  }
  bool fuga = digitalRead(DO_PIN) == LOW;   // DO baja al superar el umbral
  digitalWrite(ALARMA, fuga);
  Serial.print("Nivel: ");
  Serial.print(nivel);
  Serial.println(fuga ? "   ¡GAS DETECTADO!" : "   normal");
  delay(300);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('gas1', 'gas-sensor', { gas: 0, threshold: 0.4 }, at(BB, 'j11', 'gas-sensor', { rot: 180 })),
        part('buzzer1', 'buzzer', { mode: 'active' }, at(BB, 'j20', 'buzzer')),
      ],
      wires: wires([
        ['bb1:f11', 'uno:5V', 'red'],
        ['bb1:f10', 'uno:GND.2', 'black'],
        ['bb1:f9', 'uno:7', 'green'],
        ['bb1:f8', 'uno:A0', 'orange'],
        ['bb1:g20', 'uno:GND.3', 'black'],
        ['bb1:g23', 'uno:8', 'yellow'],
      ]),
    },
  },
  {
    id: 'riego',
    category: CAT,
    name: 'Riego automático (humedad de suelo)',
    description: 'La horquilla se alimenta desde el pin 7 solo al medir; con la tierra seca se enciende el LED L como si fuera la bomba.',
    project: {
      name: 'Riego automático',
      files: {
        'sketch.ino': `// Riego automático: mide la humedad del suelo y "riega" (LED integrado) cuando está seca.
// El sensor se alimenta desde el pin 7 solo al medir, para que la horquilla no se corroa.
const int SENSOR_VCC = 7, AO_PIN = A0, BOMBA = LED_BUILTIN;

void setup() {
  Serial.begin(9600);
  pinMode(SENSOR_VCC, OUTPUT);
  pinMode(BOMBA, OUTPUT);
}

int leerSensor() {
  digitalWrite(SENSOR_VCC, HIGH);
  delay(10);
  int v = analogRead(AO_PIN);
  digitalWrite(SENSOR_VCC, LOW);
  return v;
}

void loop() {
  int lectura = leerSensor();   // ~1023 en aire, ~300 en agua
  int humedad = constrain(map(lectura, 1023, 300, 0, 100), 0, 100);
  bool seca = humedad < 35;
  digitalWrite(BOMBA, seca);
  Serial.print("Lectura: ");
  Serial.print(lectura);
  Serial.print("   Humedad: ");
  Serial.print(humedad);
  Serial.println(seca ? " %   regando" : " %");
  delay(500);
}
`,
      },
      parts: [UNO, part('suelo1', 'soil-moisture', { moisture: 25, threshold: 0.5 }, { x: 72, z: 10, rot: 0 })],
      wires: wires([
        ['suelo1:VCC', 'uno:7', 'red'],
        ['suelo1:GND', 'uno:GND.2', 'black'],
        ['suelo1:AO', 'uno:A0', 'orange'],
      ]),
    },
  },
  {
    id: 'llama',
    category: CAT,
    name: 'Alarma de incendio (sensor de llama)',
    description: 'El receptor infrarrojo baja AO con la llama y el comparador pone DO en LOW: se enciende el LED L.',
    project: {
      name: 'Sensor de llama',
      files: {
        'sketch.ino': `// Alarma de incendio con el sensor de llama: DO en el pin 2 y AO en A0.
// Sube la "llama" en el panel del sensor.
void setup() {
  Serial.begin(9600);
  pinMode(2, INPUT);
  pinMode(LED_BUILTIN, OUTPUT);
}

void loop() {
  int ir = analogRead(A0);              // baja cuanto más intensa o cercana es la llama
  bool fuego = digitalRead(2) == LOW;   // DO baja al pasar el umbral
  digitalWrite(LED_BUILTIN, fuego);
  Serial.print("IR: ");
  Serial.print(ir);
  Serial.println(fuego ? "   ¡FUEGO!" : "");
  delay(200);
}
`,
      },
      parts: [UNO, part('llama1', 'flame-sensor', { flame: 0, threshold: 0.5 }, { x: 92, z: 8, rot: 0 })],
      wires: wires([
        ['llama1:VCC', 'uno:5V', 'red'],
        ['llama1:GND', 'uno:GND.2', 'black'],
        ['llama1:DO', 'uno:2', 'green'],
        ['llama1:AO', 'uno:A0', 'orange'],
      ]),
    },
  },
  {
    id: 'nivel-agua',
    category: CAT,
    name: 'Nivel de agua',
    description: 'S da un voltaje que crece con la parte sumergida; el programa lo traduce a seco, bajo, medio o alto.',
    project: {
      name: 'Nivel de agua',
      files: {
        'sketch.ino': `// Sensor de nivel de agua: S en A0. Cambia la parte sumergida en el panel del sensor.
void setup() {
  Serial.begin(9600);
}

void loop() {
  int v = analogRead(A0);
  Serial.print("Lectura: ");
  Serial.print(v);
  if (v < 50) Serial.println("   seco");
  else if (v < 350) Serial.println("   nivel bajo");
  else if (v < 550) Serial.println("   nivel medio");
  else Serial.println("   nivel alto");
  delay(300);
}
`,
      },
      parts: [UNO, part('agua1', 'water-level', { level: 12 }, { x: 103, z: 10, rot: 0 })],
      wires: wires([
        ['agua1:S', 'uno:A0', 'orange'],
        ['agua1:VCC', 'uno:5V', 'red'],
        ['agua1:GND', 'uno:GND.2', 'black'],
      ]),
    },
  },

  // Inclinación, Hall y 1-Wire -------------------------------------------------------------------------
  {
    id: 'inclinacion',
    category: CAT,
    name: 'Alarma de inclinación (SW-520D)',
    description: 'El interruptor de bolitas está cerrado derecho; haz clic en él para inclinarlo y se enciende el LED L.',
    project: {
      name: 'Inclinación',
      files: {
        'sketch.ino': `// Alarma de inclinación con el SW-520D entre el pin 2 y GND (pull-up interno).
// Derecho: contacto cerrado (LOW). Inclinado: abierto (HIGH). Haz clic en el sensor para inclinarlo.
const int TILT = 2;
int anterior = -1;

void setup() {
  Serial.begin(9600);
  pinMode(TILT, INPUT_PULLUP);
  pinMode(LED_BUILTIN, OUTPUT);
}

void loop() {
  int inclinado = digitalRead(TILT);
  digitalWrite(LED_BUILTIN, inclinado);
  if (inclinado != anterior) {
    Serial.println(inclinado ? "¡Inclinado!" : "Derecho");
    anterior = inclinado;
    delay(50);   // las bolitas rebotan al moverse
  }
}
`,
      },
      parts: [UNO, BB, part('tilt1', 'tilt-switch', { tilted: false }, at(BB, 'j10', 'tilt-switch'))],
      wires: wires([
        ['bb1:f10', 'uno:2', 'yellow'],
        ['bb1:f11', 'uno:GND.2', 'black'],
      ]),
    },
  },
  {
    id: 'hall-contador',
    category: CAT,
    name: 'Contador con sensor Hall (KY-003)',
    description: 'Cada vez que acercas el imán (clic en el sensor) S baja y una interrupción suma una vuelta.',
    project: {
      name: 'Contador Hall',
      files: {
        'sketch.ino': `// Contador con el sensor de efecto Hall KY-003: S en el pin 2 (interrupción 0).
// S baja cuando el imán se acerca. Haz clic en el sensor para acercar o retirar el imán.
volatile unsigned long vueltas = 0;
unsigned long mostradas = 0;

void contar() {
  vueltas++;
}

void setup() {
  Serial.begin(9600);
  pinMode(2, INPUT);   // el módulo ya trae su pull-up
  attachInterrupt(digitalPinToInterrupt(2), contar, FALLING);
  Serial.println("Acerca el imán...");
}

void loop() {
  noInterrupts();
  unsigned long n = vueltas;
  interrupts();
  if (n != mostradas) {
    mostradas = n;
    Serial.print("Vueltas: ");
    Serial.println(n);
  }
}
`,
      },
      parts: [UNO, part('hall1', 'hall-sensor', { magnet: false }, { x: 80, z: 10, rot: 0 })],
      wires: wires([
        ['hall1:S', 'uno:2', 'green'],
        ['hall1:VCC', 'uno:5V', 'red'],
        ['hall1:GND', 'uno:GND.2', 'black'],
      ]),
    },
  },
  {
    id: 'ds18b20',
    category: CAT,
    name: 'Dos termómetros DS18B20 (1-Wire)',
    description: 'Dos DS18B20 comparten el pin 2 con una sola resistencia de 4.7 kΩ. DallasTemperature los encuentra por su dirección de 64 bits.',
    project: {
      name: 'DS18B20',
      files: {
        'sketch.ino': `// Dos sensores DS18B20 en el mismo bus 1-Wire (pin 2) con una resistencia de 4.7 kΩ a 5 V.
// Librerías OneWire y DallasTemperature.
#include <OneWire.h>
#include <DallasTemperature.h>

OneWire bus(2);
DallasTemperature sensores(&bus);

void imprimirDireccion(DeviceAddress d) {
  for (int i = 0; i < 8; i++) {
    if (d[i] < 16) Serial.print('0');
    Serial.print(d[i], HEX);
  }
}

void setup() {
  Serial.begin(9600);
  sensores.begin();
  Serial.print("Sensores encontrados: ");
  Serial.println(sensores.getDeviceCount());
  DeviceAddress d;
  for (int i = 0; i < sensores.getDeviceCount(); i++) {
    sensores.getAddress(d, i);
    Serial.print("  #");
    Serial.print(i);
    Serial.print(": ");
    imprimirDireccion(d);
    Serial.println();
  }
}

void loop() {
  sensores.requestTemperatures();   // todos convierten a la vez (750 ms a 12 bits)
  for (int i = 0; i < sensores.getDeviceCount(); i++) {
    Serial.print("T");
    Serial.print(i);
    Serial.print(" = ");
    Serial.print(sensores.getTempCByIndex(i), 2);
    Serial.print(" °C   ");
  }
  Serial.println();
  delay(1000);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('t1', 'ds18b20', { temperature: 21.5 }, at(BB, 'j6', 'ds18b20')),
        part('t2', 'ds18b20', { temperature: 26 }, at(BB, 'j12', 'ds18b20')),
        part('r1', 'resistor', { value: 4700 }, at(BB, 'h7', 'resistor')),
      ],
      wires: wires([
        ['uno:5V', rail('bp', 2), 'red'],
        ['uno:GND.2', rail('bn', 3), 'black'],
        ['bb1:i6', rail('bn', 6), 'black'],
        ['bb1:i8', rail('bp', 8), 'red'],
        ['bb1:i12', rail('bn', 12), 'black'],
        ['bb1:i14', rail('bp', 14), 'red'],
        ['bb1:g11', rail('bp', 11), 'red'],
        ['bb1:f7', 'bb1:f13', 'yellow'],
        ['bb1:g13', 'uno:2', 'yellow'],
      ]),
    },
  },
];
